import * as React from 'react';
import { Button, Modal, Stack, Tab, Tabs } from 'react-bootstrap';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  addControlColumn,
  CalendarView,
  CalendarViewEvent,
  ColourKey,
  ColourKeyItem,
  Display,
  IsAllowed,
  List,
  NavIconContainer,
  PageTitle,
  QueryMulti,
  QuerySingle,
  Section,
  Uneditable,
  useCacheQuery,
  useCacheRecord,
  useCalendarRange,
} from 'gossamer-front-end';
import {
  ConvoUserView,
  Event,
  OrgUnit,
  OrgUnitEventView,
  OrgUnitEventViewRow,
  OrgUnitMember,
  OrgUnitMemberRow,
  VisibilityLimitOptions,
} from 'cb-model';
import { CreateForumConvoModal } from '../convo/CreateForumConvoModal';
import { displayLocaleTime, getFilter, getListRowLink } from 'gossamer-unified';
import { AddEventToOrgUnitButton } from './AddEventToOrgUnitButton';
import { AddMemberToOrgUnitButton } from './AddMemberToOrgUnitButton';
import { RemoveEventFromOrgUnitButton } from './RemoveEventFromOrgUnitButton';
import { RemoveMemberFromOrgUnitButton } from './RemoveMemberFromOrgUnitButton';
import { DeleteButton } from 'packages/shared-front-end';

const getNowMinus7Days = () => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString();
};

export const OrgUnitDisplay = (): React.JSX.Element => {
  const params = useParams();
  const now = React.useRef(new Date().toISOString());
  const nowMinus7Days = React.useRef(getNowMinus7Days());
  const record = useCacheRecord(OrgUnit, params.key);
  const [searchParams, setSearchParams] = useSearchParams();

  return (
    <>
      <NavIconContainer entityId={OrgUnit.id} />
      <PageTitle verb="Display" entity={OrgUnit} keyValue={params.key} subtitle="Details relating to this Org Unit" />

      <Tabs
        activeKey={searchParams.get('tab') || 'main'}
        className="mb20px"
        mountOnEnter={true}
        unmountOnExit={true}
        variant="underline"
        onSelect={(tab) => {
          const newParams = new URLSearchParams();
          newParams.set('tab', tab);
          setSearchParams(newParams);
        }}
      >
        <Tab eventKey="main" title="Main Details">
          <Section>
            <QuerySingle entity={OrgUnit} keyValue={params.key} render={Display} />
            <Stack direction="horizontal" gap={2}>
              <IsAllowed entity={OrgUnit} action="update">
                <Link to={`/admin/org-unit/update/${params.key}`}>
                  <Button size="sm" variant="primary">
                    Update
                  </Button>
                </Link>
              </IsAllowed>
              <IsAllowed entity={OrgUnit} action="delete">
                <DeleteButton
                  entityId="OrgUnit"
                  keyValue={params.key}
                  followOnUrl="/admin/org-unit/search"
                  otherEntitiesToReset={['OrgUnitView']}
                />
              </IsAllowed>
            </Stack>
          </Section>

          {/* <Section>
                  {!!record.data && (
                    <Summary orgUnitId={parseInt(params.key, 10)} />
                  )}
                </Section> */}

          {/* <Section
                  title="Activity Overview - Near Future"
                  subtitle="Number of events scheduled per week, and number of bookings for those events"
                >
                  <QueryMulti
                    entity={ReportOrgUnitView}
                    alterEntity={(localEntity: typeof ReportOrgUnitView) => {
                      localEntity.fields.institution_group_id.listColumn = false;
                      localEntity.fields.attendance_count.listColumn = false;
                      localEntity.fields.attendance_rate.listColumn = false;
                    }}
                    queryOptions={{
                      where: {
                        and: [
                          getFilter('institution_group_id', 'eq', params.key),
                          getFilter('week_commencing', 'gt', nowMinus7Days.current),
                        ],
                      },
                      order: [['week_commencing', 'ASC']],
                      limit: 6,
                    }}
                    render={List}
                  />
                </Section>
      
                <Section
                  title="Activity Overview - Recent Past"
                  subtitle="Number of events scheduled per week, number of bookings for those events, attendances for those bookings and certificates produced"
                >
                  <QueryMulti
                    entity={ReportOrgUnitView}
                    alterEntity={(localEntity: typeof ReportOrgUnitView) => {
                      localEntity.fields.institution_group_id.listColumn = false;
                    }}
                    queryOptions={{
                      where: {
                        and: [
                          getFilter('institution_group_id', 'eq', params.key),
                          getFilter('week_commencing', 'lt', now.current),
                        ],
                      },
                      order: [['week_commencing', 'DESC']],
                      limit: 6,
                    }}
                    render={List}
                  />
                </Section> */}

          <Section title="Child Org Units" subtitle="Units directly below this unit">
            <QueryMulti
              entity={OrgUnit}
              queryOptions={{
                where: {
                  and: [getFilter('parent_org_unit_id', 'eq', params.key)],
                },
              }}
              render={List}
            />
          </Section>

          <Section title="Conversations" subtitle="Message threads available to members in this unit">
            <QueryMulti
              entity={ConvoUserView}
              queryOptions={{
                where: {
                  and: [getFilter('forum_type', 'eq', 'OrgUnit'), getFilter('forum_key', 'eq', params.key)],
                },
              }}
              render={List}
            />
            {record.data && (
              <CreateForumConvoModal
                forumType="OrgUnit"
                forumKey={params.key}
                institutionId={record.data.institution_id}
              />
            )}
          </Section>
        </Tab>

        <Tab eventKey="members" title="Members and Admins">
          <Section title="Group Admins" subtitle="Users who can administer this org unit">
            <QueryMulti
              entity={OrgUnitMember}
              alterEntity={(localEntity: typeof OrgUnitMember) => {
                localEntity.fields.org_unit_id.listColumn = false;
              }}
              queryOptions={{
                where: {
                  and: [getFilter('org_unit_id', 'eq', params.key), getFilter('is_admin', 'eq', true)],
                },
                order: [['created_at', 'DESC']],
              }}
              render={List}
            />
            <IsAllowed entity={OrgUnitMember} action="update">
              <Link to={`/admin/org-unit/admins-grid/${params.key}`}>
                <Button size="sm" variant="primary">
                  Update
                </Button>
              </Link>
            </IsAllowed>
          </Section>

          <Section title="Members" subtitle="Most recently joined members">
            <QueryMulti
              entity={OrgUnitMember}
              alterEntity={(localEntity: typeof OrgUnitMember) => {
                localEntity.fields.org_unit_id.listColumn = false;
                addControlColumn(
                  localEntity,
                  'remove',
                  999,
                  (row: OrgUnitMemberRow) => String(row.id),
                  RemoveMemberFromOrgUnitButton,
                );
              }}
              queryOptions={{
                where: {
                  and: [getFilter('org_unit_id', 'eq', params.key)],
                },
                order: [['created_at', 'DESC']],
              }}
              render={List}
            />
            <IsAllowed entity={OrgUnitMember} action="update">
              <div className="mb20px" />
              {!!record.data && <AddMemberToOrgUnitButton orgUnitId={parseInt(params.key)} />}
            </IsAllowed>
          </Section>

          {/* <Section title="Task Progress Summary" subtitle="How this group's members are getting on with their tasks">
            <QuerySingle
              entity={TaskGroupView}
              keyValue={params.key}
              render={Display}
              fieldGroup="summary"
              renderNotFound={() => <div>No tasks</div>}
            />
          </Section> */}
          {/* <Section title="Task Progress by Member" subtitle="How these members are getting on with their tasks">
            <QueryMulti
              entity={TaskUserGroupView}
              alterEntity={(localEntity: typeof TaskUserGroupView) => {
                localEntity.fields.institution_id.listColumn = false;
              }}
              queryOptions={{
                where: {
                  and: [getFilter('institution_group_id', 'eq', params.key)],
                },
              }}
              render={List}
            />
          </Section> */}
        </Tab>
        <Tab eventKey="events" title="Events">
          <Section title="Events" subtitle="Events happening for members of this org unit">
            <EventCalendar org_unit_id={parseInt(params.key)} />
            <IsAllowed entity={OrgUnitMember} action="update">
              <div className="mb20px" />
              {!!record.data && (
                <Stack direction="vertical" gap={3}>
                  <AddEventToOrgUnitButton orgUnitId={parseInt(params.key)} />
                  {/* <AllEventsChangeHostButton institutionGroupId={parseInt(params.key)} />
                  <AllEventsAddCoHostButton institutionGroupId={parseInt(params.key)} />
                  <Link to={`/admin/institution-group/event-bulk-copy/${params.key}`}>
                    <Button size="sm" variant="primary">
                      Bulk Copy Events into this Group
                    </Button>
                  </Link> */}
                </Stack>
              )}
            </IsAllowed>
          </Section>
        </Tab>
      </Tabs>
    </>
  );
};

interface OrgUnitCalendarEvent extends CalendarViewEvent {
  colorCode: string;
}

const ORG_UNIT_EVENT_COLORS = {
  public: '#2563EB',
  private: '#7C3AED',
  'private-group': '#0D9488',
  closed: '#475569',
  cancelled: '#DC2626',
};

const ORG_UNIT_EVENT_LEGEND: ColourKeyItem[] = VisibilityLimitOptions.map((option) => ({
  id: option.id,
  label: option.label,
  bgColor: ORG_UNIT_EVENT_COLORS[option.id as keyof typeof ORG_UNIT_EVENT_COLORS] || ORG_UNIT_EVENT_COLORS.private,
}));

const EventCalendar = (props: { org_unit_id: number }): React.JSX.Element => {
  const [showingEvent, setShowingEvent] = React.useState<OrgUnitCalendarEvent | null>(null);
  const { range } = useCalendarRange();
  const navigate = useNavigate();
  const [stableEventRows, setStableEventRows] = React.useState<OrgUnitEventViewRow[]>([]);
  const eventQuery = useCacheQuery(OrgUnitEventView, {
    where: {
      and: [
        getFilter('org_unit_id', 'eq', String(props.org_unit_id)),
        getFilter('start_at', 'gt', range.start.toISOString()),
        getFilter('start_at', 'lt', range.end.toISOString()),
      ],
    },
    limit: 1000,
  });
  React.useEffect(() => {
    if (eventQuery.data !== undefined) {
      setStableEventRows(eventQuery.data);
    }
  }, [eventQuery.data]);
  const calendarRows = eventQuery.data ?? stableEventRows;
  const events: OrgUnitCalendarEvent[] = calendarRows.map((e) => ({
    id: String(e.id),
    title: e.title || 'Event',
    start: new Date(e.start_at),
    end: new Date(e.end_at),
    colorCode:
      e.visibility_limit === 'cancelled'
        ? ORG_UNIT_EVENT_COLORS.cancelled
        : ORG_UNIT_EVENT_COLORS[e.visibility_limit as keyof typeof ORG_UNIT_EVENT_COLORS] ||
          ORG_UNIT_EVENT_COLORS.private,
  }));
  return (
    <>
      <CalendarView<OrgUnitCalendarEvent>
        events={events}
        handleEventSelect={(event) => setShowingEvent(event)}
        eventPropGetter={(event) => ({
          style: {
            backgroundColor: event.colorCode,
            borderColor: event.colorCode,
            color: '#FFFFFF',
          },
        })}
      />
      <ColourKey items={ORG_UNIT_EVENT_LEGEND} />
      <Modal
        show={!!showingEvent}
        onHide={() => setShowingEvent(null)}
        aria-labelledby="group-event-modal-title"
        size="sm"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="group-event-modal-title" as="h5">
            {showingEvent?.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Stack direction="horizontal" gap={2}>
            <Uneditable field={OrgUnitEventView.fields.start_at} value={showingEvent?.start?.toISOString()} />
            -&nbsp;
            {showingEvent?.end ? displayLocaleTime(showingEvent?.end) : 'N/A'}
          </Stack>
          <br />
          <Stack direction="horizontal" gap={2}>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                const eventId = calendarRows.find((e) => String(e.id) === showingEvent?.id)?.event_id;
                if (!eventId) {
                  return;
                }
                navigate(getListRowLink(Event.listRowLink, String(eventId)));
              }}
            >
              Go to Event
            </Button>
            <RemoveEventFromOrgUnitButton value={showingEvent?.id} handleSuccess={() => setShowingEvent(null)} />
          </Stack>
        </Modal.Body>
      </Modal>
    </>
  );
};
